import type { Competitor } from '../types';

// Researched 2026-11-03. Every claim links to the page it came from.
// To correct something: change it here, update sourceUrl and lastReviewed.

export const lifetimely: Competitor = {
  "id": "lifetimely",
  "name": "Lifetimely",
  "group": "ecomm",
  "website": "https://useamp.com/products/analytics",
  "owner": "AMP (Singapore-based Shopify app company; acquired Lifetimely November 2022 per third-party review)",
  "summary": "Shopify app for daily profit and loss, customer lifetime value and cohort analysis, now sold by AMP as Lifetimely Profit Agent and LTV. Built for small and mid-size Shopify brands that want margin and LTV reporting with little setup.",
  "bestFor": [
    "Shopify brands that want daily P&L, contribution margin and LTV reports at low cost",
    "Smaller stores (up to a few thousand orders a month) where an entry price of $0-$149/month matters",
    "Subscription brands on Recharge, Skio, Stay AI or Loop that want LTV and repurchase analysis"
  ],
  "notFor": [
    "Non-Shopify storefronts (WooCommerce, BigCommerce, headless) per third-party review",
    "Brands that need ad-level attribution or intraday marketing decisions",
    "Brands with wholesale, ERP or retail revenue, or that need their data in their own warehouse"
  ],
  "strengths": [
    "Free plan with daily P&L, LTV prediction, custom reports and benchmarks, and paid plans from $49/month, far below the cost of a custom warehouse project",
    "Priced on order volume rather than revenue, and every plan includes all features",
    "Setup takes about 1-2 hours plus roughly a day of historical backfill (third-party review)",
    "Ready-made profit reporting that pulls ad spend (Meta, Google, TikTok), Shopify costs, ShipBob/ShipStation shipping costs and QuickBooks custom costs",
    "Predictive LTV model, cohort, repurchase and CAC payback reports out of the box",
    "Benchmarks against other stores, which a single-brand warehouse cannot provide",
    "Profit Agent AI monitors the store and proposes actions for approval; MCP access for AI tools on paid plans"
  ],
  "limitations": [
    "Shopify-only storefront support per third-party review; Amazon is a $75/month add-on",
    "No TikTok Shop, NetSuite/ERP or retail/POS connector listed on AMP's integrations page",
    "No data warehouse export or SQL access found in vendor docs; third-party review says it is not meant to replace a warehouse",
    "Data refreshes every few hours rather than in real time, and Amazon data daily (third-party review)",
    "Attribution is UTM-based reporting on vendor's attribution page; third-party review says it is not an attribution platform",
    "Profit accuracy depends on the COGS the merchant enters"
  ],
  "pricing": {
    "model": "order volume (monthly Shopify orders)",
    "publicStartingPrice": "$49/month (S plan, up to 500 orders/month); free plan up to 50 orders/month",
    "freeTier": "yes - up to 50 orders/month, includes daily P&L, LTV model, custom reports, benchmarks; no Profit Agent or MCP",
    "notes": "Tiers by monthly orders: Free (50) $0; S (500) $49; M (3,000) $149; L (7,000) $299; XL (15,000) $499; XXL (25,000) $749; Unlimited (25,000+) $999 via demo. 14-day trial on paid plans. Plan recommended from 6-month average order count; you are contacted only after two consecutive months over the limit. Add-ons: Amazon +$75/month; extra Profit Agent credits +$49/month for 20,000 credits (5,000/month included). Higher tiers get more support (account manager, consulting).",
    "sourceUrl": "https://useamp.com/pricing.md",
    "checkedOn": "2026-11-03"
  },
  "features": {
    "shopify": {
      "value": "yes",
      "note": "Core integration; pricing based on Shopify orders",
      "sourceUrl": "https://useamp.com/integrations"
    },
    "amazon": {
      "value": "partial",
      "note": "Available as a $75/month add-on on paid plans",
      "sourceUrl": "https://useamp.com/pricing.md",
      "badge": "$75/mo add-on"
    },
    "tiktok_shop": {
      "value": "unknown",
      "note": "Only TikTok ad spend listed; TikTok Shop not mentioned",
      "sourceUrl": "https://useamp.com/integrations"
    },
    "wholesale_retail_or_erp": {
      "value": "partial",
      "note": "ShipBob/ShipStation shipping costs and QuickBooks Online; no NetSuite, ERP or POS listed",
      "sourceUrl": "https://useamp.com/integrations"
    },
    "ad_platforms": {
      "value": "yes",
      "note": "Meta, Google Ads and TikTok ad spend",
      "sourceUrl": "https://useamp.com/integrations"
    },
    "first_party_pixel_attribution": {
      "value": "partial",
      "note": "Shopify listing mentions pixel tracking, but vendor attribution page describes UTM-based reporting with no multi-touch model named",
      "sourceUrl": "https://useamp.com/products/analytics/attribution"
    },
    "mmm_incrementality": {
      "value": "unknown",
      "note": "Not mentioned in vendor pages checked",
      "sourceUrl": "https://useamp.com/pricing.md"
    },
    "profit_cogs": {
      "value": "yes",
      "note": "Daily P&L with costs, shipping and ad spend",
      "sourceUrl": "https://useamp.com/pricing.md"
    },
    "ltv_cohorts": {
      "value": "yes",
      "note": "Predictive LTV, cohorts, repurchase rate, CAC payback",
      "sourceUrl": "https://useamp.com/pricing.md"
    },
    "subscription_analytics": {
      "value": "yes",
      "note": "Recharge, Skio, Stay AI and Loop integrations",
      "sourceUrl": "https://useamp.com/integrations"
    },
    "warehouse_access": {
      "value": "unknown",
      "note": "No warehouse export or SQL access found in vendor docs",
      "sourceUrl": "https://useamp.com/integrations"
    },
    "custom_metrics": {
      "value": "partial",
      "note": "Custom reports/dashboards and a custom-metrics help category; extent of formula logic not verified",
      "sourceUrl": "https://help.useamp.com/category/646-getting-started-with-lifetimely"
    },
    "custom_sql": {
      "value": "unknown",
      "note": "SQL not mentioned in vendor docs checked",
      "sourceUrl": "https://help.useamp.com/category/646-getting-started-with-lifetimely"
    },
    "ai_assistant": {
      "value": "yes",
      "note": "Profit Agent (credit-based) and Lifetimely MCP on paid plans",
      "sourceUrl": "https://useamp.com/pricing.md"
    },
    "mobile_app": {
      "value": "unknown",
      "note": "No mobile app mentioned",
      "sourceUrl": "https://apps.shopify.com/lifetimely-lifetime-value-and-profit-analytics"
    }
  },
  "recentChanges": [
    {
      "date": "2022-11",
      "change": "Acquired by AMP; now sold as Lifetimely Profit Agent and LTV by AMP alongside AMP's Upsell and Back in Stock apps (per third-party review)",
      "sourceUrl": "https://eightx.co/blog/compare/reviews/lifetimely-for-ecommerce-review"
    },
    {
      "date": "2026-04",
      "change": "Pricing policy updated: order-based tiers from Free to Unlimited ($999), all features on all plans",
      "sourceUrl": "https://help.useamp.com/article/689-pricing-policy"
    },
    {
      "date": "2026-10",
      "change": "Pricing page updated with new S plan ($49, 500 orders), Profit Agent AI with monthly credits, Lifetimely MCP and a $49 credit add-on",
      "sourceUrl": "https://useamp.com/pricing.md"
    }
  ],
  "sources": [
    {
      "title": "AMP: Lifetimely pricing (machine-readable, updated 2026-10-30)",
      "url": "https://useamp.com/pricing.md"
    },
    {
      "title": "AMP help: Lifetimely pricing policy",
      "url": "https://help.useamp.com/article/689-pricing-policy"
    },
    {
      "title": "AMP integrations",
      "url": "https://useamp.com/integrations"
    },
    {
      "title": "AMP: Lifetimely attribution",
      "url": "https://useamp.com/products/analytics/attribution"
    },
    {
      "title": "AMP help: Getting started with Lifetimely",
      "url": "https://help.useamp.com/category/646-getting-started-with-lifetimely"
    },
    {
      "title": "Lifetimely on Shopify App Store",
      "url": "https://apps.shopify.com/lifetimely-lifetime-value-and-profit-analytics"
    },
    {
      "title": "Eightx: Lifetimely review (third-party, June 2026)",
      "url": "https://eightx.co/blog/compare/reviews/lifetimely-for-ecommerce-review"
    }
  ],
  "lastReviewed": "2026-11-03"
};
